import {
  Backdrop,
  Box,
  Button,
  Divider,
  Fade,
  makeStyles,
  Modal,
  Typography,
  useTheme,
} from '@material-ui/core'
import Paper from '@material-ui/core/Paper'
import clsx from 'clsx'
import { chunk } from 'lodash'
import React, { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { OptionType } from '../../types'

export const useMobileAutocompleteStyles = makeStyles((theme) => ({
  container: {
    width: '94%',
    maxWidth: theme.spacing(62),
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    backgroundColor: 'rgb(242, 242, 242)',
    borderRadius: 14,
    textAlign: 'center',
    outline: 'none',
    overflow: 'hidden',
  },
  header: {
    padding: theme.spacing(2),
    fontWeight: 600,
    color: '#8E8E93',
    fontSize: 14,
    textTransform: 'lowercase',
    '&::first-letter': {
      textTransform: 'uppercase',
    },
  },
  button: {
    fontSize: 20,
    fontWeight: 400,
    minHeight: theme.spacing(7),
    flex: 1,
    '&.checked': {
      fontWeight: 800,
      backgroundColor: theme.palette.background.paper,
    },
  },
  cancelContainer: {
    height: theme.spacing(7),
    marginTop: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 14,
    display: 'flex',
  },
  cancelButton: {
    padding: theme.spacing(2),
    fontSize: 20,
  },
  optionsContainer: {
    overflow: 'auto',
    maxHeight: '70vh',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
  },
}))

export const MobileAutocompleteModal: FC<{
  open: boolean
  onClose: () => void
  onSelect: (value: string | null) => () => void
  textHeader: string | undefined
  options: OptionType[]
  currentValue: string | undefined
  doubleRows?: boolean
  isDynamicLabels?: boolean
}> = ({
  open,
  onClose,
  onSelect,
  textHeader,
  options,
  currentValue,
  doubleRows,
  isDynamicLabels,
}) => {
  const classes = useMobileAutocompleteStyles()
  const { t } = useTranslation()
  const theme = useTheme()

  const doubleRowOptions = useMemo(() => (doubleRows ? chunk(options, 2) : null), [
    options,
    doubleRows,
  ])

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={classes.modal}
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
      style={{ zIndex: theme.zIndex.modal + 10 }}
    >
      <Fade in={open}>
        <Box className={classes.container}>
          <Paper className={classes.paper}>
            <Typography className={classes.header}>{`${t('select', 'Select')} ${t(
              textHeader || '',
            )}`}</Typography>
            <Divider />
            <Box className={classes.optionsContainer}>
              {doubleRowOptions
                ? doubleRowOptions.map((pair, idx) => (
                    <React.Fragment key={idx}>
                      <Box className={classes.row}>
                        <Button
                          className={clsx(
                            classes.button,
                            currentValue === pair[0]?.key && 'checked',
                          )}
                          onClick={pair[0] ? onSelect(pair[0].key) : undefined}
                        >
                          <Typography variant="h4">{t(`${pair[0]?.label}`)}</Typography>
                        </Button>
                        {pair[1] && <Divider orientation="vertical" flexItem />}
                        {pair[1] && (
                          <Button
                            className={clsx(
                              classes.button,
                              currentValue === pair[1]?.key && 'checked',
                            )}
                            onClick={onSelect(pair[1].key)}
                          >
                            <Typography variant={'h4'}>{t(`${pair[1].label}`)}</Typography>
                          </Button>
                        )}
                      </Box>
                      <Divider />
                    </React.Fragment>
                  ))
                : options.map(({ key, label }) => (
                    <React.Fragment key={key}>
                      <Button
                        className={clsx(classes.button, currentValue === key && 'checked')}
                        fullWidth
                        onClick={onSelect(key)}
                      >
                        {isDynamicLabels ? label : t(`${label}`)}
                      </Button>
                      <Divider />
                    </React.Fragment>
                  ))}
            </Box>
          </Paper>
          <Box className={classes.cancelContainer}>
            <Button className={classes.cancelButton} fullWidth onClick={onClose}>
              {t('cancel', 'Cancel')}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  )
}
